/**
 * Facebook Ads Adapter (Story 9.3)
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 * Uses safeFetch() for all external API calls.
 *
 * CRITICAL: Marketing API uses the user's long-lived access token directly.
 * This is the OPPOSITE of Facebook Pages (Story 9.2) which uses Page Access Tokens.
 * credentials.accessToken IS the correct token for all Marketing API calls.
 *
 * ADR-013: Adapter Pattern for API Isolation
 */

import type {
  DimensionDefinition,
  IntegrationAdapter,
  AdapterContext,
  OAuthCredentials,
  AdapterResult,
  AdapterMetrics,
  AccountInfo,
  MetricDefinition,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { META_GRAPH_API_VERSION, META_INTEGRATION_SCOPES } from "./scopes";
import {
  discoverMetaAccounts,
  parseAppUsage,
  type MetaDiscoveryFirstError,
  type MetaSectionStatus,
} from "./discovery";
import { parseMetaGraphError, META_ATTRIBUTION_SETTING_MISMATCH_SUBCODE } from "./errors";

// ============================================================================
// Constants
// ============================================================================

export const META_GRAPH_BASE = `https://graph.facebook.com/${META_GRAPH_API_VERSION}`;

/** Rate limit threshold — stop making calls if x-app-usage call_count > 80% */
export const RATE_LIMIT_THRESHOLD = 80;

/** Maximum data retention for Marketing API (37 months) */
export const MAX_RETENTION_MONTHS = 37;

/** Maximum pages to follow in cursor-based pagination */
export const MAX_PAGES = 10;

/** Purchase action types used for filtering purchase-specific metrics */
export const PURCHASE_ACTION_TYPES = ["offsite_conversion.fb_pixel_purchase", "purchase"];

// ============================================================================
// Types (local to adapter, NOT exported)
// ============================================================================

/**
 * Story 35.5 — per-window-aware AdsActionStats entry shape.
 *
 * Marketing API v25/v26 returns `actions[]`, `action_values[]` and
 * `cost_per_action_type[]` as arrays of AdsActionStats entries that expose
 * `value` (default-attribution-window sum) PLUS per-window splits.
 *
 * `7d_view` and `28d_view` were removed by Meta on Jan 12, 2026, but the
 * AdsActionStats typedef still falsely lists them for backwards-compat —
 * Meta did not update the typedef; they just stopped populating values.
 * Read these fields defensively (treat undefined as 0).
 *
 * Source: developers.facebook.com/docs/marketing-api/reference/ads-action-stats
 * verified 2026-05-19.
 */
export interface ActionStatsEntry {
  action_type: string;
  value: string;
  "1d_click"?: string;
  "7d_click"?: string;
  "28d_click"?: string;
  "1d_view"?: string;
  // 7d_view + 28d_view removed Jan 12, 2026 — defensive type only;
  // expected to be absent on live responses.
  "7d_view"?: string;
  "28d_view"?: string;
}

/**
 * Story 35.5 — v26 attribution-setting branch discriminator.
 *
 * v26.0 (April 8, 2026 pre-release) introduced the `attribution_setting`
 * field on the Ad Account object. Possible values:
 *   - "use_account_default" → insights MUST pass
 *     `use_account_attribution_setting=true` AND MUST OMIT
 *     `action_attribution_windows`
 *   - "configured_at_call_time" → per-call `action_attribution_windows`
 *     override still works (legacy path)
 *   - undefined → legacy pre-v26 accounts that don't expose the field;
 *     adapter defaults to "configured_at_call_time" (legacy-safe behavior)
 *
 * Source: developers.facebook.com/docs/graph-api/changelog/version26.0
 * verified 2026-05-19.
 */
export type AttributionSetting = "use_account_default" | "configured_at_call_time";

/**
 * KV key for caching the ad account's attribution_setting probe result.
 *
 * Story 35.5 invariant: the FB Ads and IG Ads adapters use the SAME KV key
 * because they share the same ad account on Meta's side. The KV value is
 * the attribution_setting for the ad account itself, not for a per-platform
 * surface. This is why the `meta:meta:` prefix uses the OAuth family name
 * (not the platform name) — both adapters belong to the `meta` family.
 *
 * Round 1 code review (H4): cross-adapter consequence — if IG Ads'
 * validateConnection runs first and writes the (potentially stale) cached
 * value, FB Ads' next fetchMetrics will read that value too. The self-heal
 * flow recovers if the cached value is wrong (subcode 2655 → KV refresh +
 * retry once), so the only downside is one extra failed request before
 * recovery. Acceptable trade-off vs. duplicating KV state per adapter.
 */
export const attributionSettingKvKey = (integrationId: string): string =>
  `meta:meta:${integrationId}.attributionSetting`;

/** TTL for the attribution_setting KV cache — 24h. Meta accounts rarely change mid-day. */
export const ATTRIBUTION_SETTING_TTL_SECONDS = 86400;

/**
 * Story 35.5 — 4 active Meta attribution windows post Jan 12, 2026.
 * `7d_view` + `28d_view` were removed by Meta on Jan 12, 2026.
 */
export const ACTIVE_ATTRIBUTION_WINDOWS = ["1d_click", "7d_click", "28d_click", "1d_view"] as const;
export type AttributionWindow = (typeof ACTIVE_ATTRIBUTION_WINDOWS)[number];

/**
 * Story 35.5 — the 6 highest-agency-value action types for per-window splits.
 * Maps the FB_ADS_METRICS key prefix to the Meta wire-format `action_type`.
 * Selecting only 6 of the 15 IMC-14 action types keeps the matrix bounded
 * (6 × 4 windows = 24 per-window metrics, NOT 15 × 4 = 60 which would
 * explode the FB_ADS_METRICS catalog past its 100-entry sanity cap).
 */
export const PER_WINDOW_ACTION_TYPES: ReadonlyArray<{ keyPrefix: string; wireType: string; label: string }> = [
  { keyPrefix: "action_purchase", wireType: "offsite_conversion.fb_pixel_purchase", label: "Purchases" },
  { keyPrefix: "action_lead", wireType: "offsite_conversion.fb_pixel_lead", label: "Leads" },
  { keyPrefix: "action_add_to_cart", wireType: "offsite_conversion.fb_pixel_add_to_cart", label: "Add to Cart" },
  { keyPrefix: "action_initiate_checkout", wireType: "offsite_conversion.fb_pixel_initiate_checkout", label: "Checkout Started" },
  { keyPrefix: "action_view_content", wireType: "offsite_conversion.fb_pixel_view_content", label: "Content Views" },
  { keyPrefix: "action_complete_registration", wireType: "offsite_conversion.fb_pixel_complete_registration", label: "Registrations" },
];

/**
 * Story 35.5 — the 2 omni-variant action types for per-window splits.
 * Mirrors PER_WINDOW_ACTION_TYPES shape. Omni variants aggregate online +
 * offline + cross-channel surface conversions.
 */
export const PER_WINDOW_OMNI_ACTION_TYPES: ReadonlyArray<{ keyPrefix: string; wireType: string; label: string }> = [
  { keyPrefix: "action_omni_purchase", wireType: "omni_purchase", label: "Cross-channel Purchases" },
  { keyPrefix: "action_omni_view_content", wireType: "omni_view_content", label: "Cross-channel Content Views" },
];

/**
 * Story 35.5 — short-label suffix for each attribution window (used when
 * synthesizing MetricDefinition labels).
 */
export const ATTRIBUTION_WINDOW_LABEL_SUFFIX: Record<AttributionWindow, string> = {
  "1d_click": "1d click",
  "7d_click": "7d click",
  "28d_click": "28d click",
  "1d_view": "1d view",
};

export interface MarketingInsightsResponse {
  data?: Array<{
    date_start: string;
    date_stop: string;
    impressions?: string;
    reach?: string;
    frequency?: string;
    clicks?: string;
    ctr?: string;
    cpc?: string;
    spend?: string;
    cpm?: string;
    cpp?: string;
    inline_link_clicks?: string;
    inline_link_click_ctr?: string;
    inline_post_engagement?: string;
    cost_per_inline_post_engagement?: string;
    actions?: ActionStatsEntry[];
    action_values?: ActionStatsEntry[];
    cost_per_action_type?: ActionStatsEntry[];
    purchase_roas?: Array<{ action_type: string; value: string }>;
    website_purchase_roas?: Array<{ action_type: string; value: string }>;
    video_thru_play_actions?: Array<{ action_type: string; value: string }>;
    video_p25_watched_actions?: Array<{ action_type: string; value: string }>;
    video_p50_watched_actions?: Array<{ action_type: string; value: string }>;
    video_p75_watched_actions?: Array<{ action_type: string; value: string }>;
    video_p100_watched_actions?: Array<{ action_type: string; value: string }>;
    video_continuous_2_sec_watched_actions?: Array<{ action_type: string; value: string }>;
    video_15_sec_watched_actions?: Array<{ action_type: string; value: string }>;
    video_30_sec_watched_actions?: Array<{ action_type: string; value: string }>;
    video_avg_time_watched_actions?: Array<{ action_type: string; value: string }>;
    video_play_actions?: Array<{ action_type: string; value: string }>;
    cost_per_thruplay?: string;
    cost_per_15_sec_video_view?: Array<{ action_type: string; value: string }>;
    cost_per_2_sec_continuous_video_view?: Array<{ action_type: string; value: string }>;
    outbound_clicks?: Array<{ action_type: string; value: string }>;
    outbound_clicks_ctr?: Array<{ action_type: string; value: string }>;
    cost_per_outbound_click?: Array<{ action_type: string; value: string }>;
    unique_clicks?: string;
    unique_ctr?: string;
    cost_per_unique_click?: string;
    quality_ranking?: string;
    engagement_rate_ranking?: string;
    conversion_rate_ranking?: string;
    campaign_name?: string;
    campaign_id?: string;
    full_view_impressions?: string;
    social_spend?: string;
    // Story 35.5 — ASC discriminator. `optimization_goal` returns `null`
    // for Advantage+ Shopping Campaigns; `objective` indicates the
    // campaign-level goal. Both fields are surfaced by Meta on insights
    // when the fields list requests them.
    optimization_goal?: string | null;
    objective?: string;
  }>;
  paging?: {
    next?: string;
  };
  error?: {
    message: string;
    type: string;
    code: number;
    error_subcode?: number;
  };
}

// ============================================================================
// Meta Error Response Handler
// ============================================================================

/**
 * Attempts to parse a Meta error from an HTTP error response.
 * If the response body contains a recognized Meta error.code, returns an
 * AdapterResult with the correctly mapped error code.
 * Otherwise returns null to let safeFetch()'s default mapping apply.
 */
export async function handleMetaFetchError(
  fetchResult: { success: false; error: { code: string; message: string; retryable: boolean; providerStatus?: number; providerMessage?: string } }
): Promise<AdapterResult<never>> {
  if (fetchResult.error.providerMessage) {
    const metaError = parseMetaGraphError(fetchResult.error.providerMessage);
    if (metaError) {
      return adapterErr({
        code: metaError.code,
        message: sanitizeErrorMessage(metaError.message),
        retryable: metaError.retryable,
        providerStatus: fetchResult.error.providerStatus,
      });
    }
  }

  return adapterErr(fetchResult.error as Parameters<typeof adapterErr>[0]);
}

/**
 * Maps a non-complete catalog-discovery section outcome to an AdapterResult
 * error (Story 31.20 AC #3). Strict full-catalog semantics: a truncated
 * catalog is NEVER adapterOk. `rate_limited` is special-cased retryable;
 * otherwise the section's firstError flows through the Meta error path so a
 * revoked token surfaces as token_expired (Graph error 190) rather than a
 * blanket code. Exported for reuse by the Instagram Ads adapter (same
 * pattern as handleMetaFetchError).
 */
export async function mapDiscoverySectionFailure(
  status: Exclude<MetaSectionStatus, "complete">,
  firstError: MetaDiscoveryFirstError | undefined
): Promise<AdapterResult<never>> {
  if (status === "rate_limited") {
    return adapterErr({
      code: "rate_limited",
      message: "Meta account discovery stopped: app-level rate limit threshold reached",
      retryable: true,
    });
  }
  if (firstError) {
    return handleMetaFetchError({ success: false, error: firstError });
  }
  if (status === "aborted") {
    return adapterErr({
      code: "network_error",
      message: "Meta account discovery aborted before the full catalog was fetched",
      retryable: true,
    });
  }
  return adapterErr({
    code: "invalid_response",
    message: `Meta account discovery did not complete the required section (status: ${status})`,
    retryable: false,
  });
}

// ============================================================================
// Helper: Parse Metric Values (Marketing API returns strings)
// ============================================================================

/**
 * Parses a string or number value from the Marketing API into a number.
 * Returns 0 for undefined, null, or NaN values.
 */
export function parseMetricValue(value: string | number | undefined | null): number {
  if (value === undefined || value === null) return 0;
  if (typeof value === "number") return value;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

// ============================================================================
// Helper: Action Array Parsing (CRITICAL — nested conversion data)
// ============================================================================

export function parseActions(actions: Array<{ action_type: string; value: string }> | undefined): {
  totalConversions: number;
  purchaseConversions: number;
} {
  if (!actions || actions.length === 0) return { totalConversions: 0, purchaseConversions: 0 };

  let totalConversions = 0;
  let purchaseConversions = 0;
  for (const action of actions) {
    const value = parseMetricValue(action.value);
    totalConversions += value;
    if (PURCHASE_ACTION_TYPES.includes(action.action_type)) {
      purchaseConversions += value;
    }
  }
  return { totalConversions, purchaseConversions };
}

export function parseActionValues(actionValues: Array<{ action_type: string; value: string }> | undefined): number {
  if (!actionValues) return 0;
  return actionValues
    .filter(a => PURCHASE_ACTION_TYPES.includes(a.action_type))
    .reduce((sum, a) => sum + parseMetricValue(a.value), 0);
}

export function parseCostPerAction(costPerAction: Array<{ action_type: string; value: string }> | undefined): number {
  if (!costPerAction) return 0;
  const purchaseEntry = costPerAction.find(a => PURCHASE_ACTION_TYPES.includes(a.action_type));
  return purchaseEntry ? parseMetricValue(purchaseEntry.value) : 0;
}

export function parsePurchaseRoas(purchaseRoas: Array<{ action_type: string; value: string }> | undefined): number {
  if (!purchaseRoas) return 0;
  const entry = purchaseRoas.find(a => PURCHASE_ACTION_TYPES.includes(a.action_type));
  return entry ? parseMetricValue(entry.value) : 0;
}

export function parseVideoThruplay(videoActions: Array<{ action_type: string; value: string }> | undefined): number {
  if (!videoActions) return 0;
  return videoActions.reduce((sum, a) => sum + parseMetricValue(a.value), 0);
}

/**
 * Parses an action array and sums all values (regardless of action_type).
 * Used for video quartile arrays, outbound clicks, etc.
 */
export function parseActionArrayTotal(actions: Array<{ action_type: string; value: string }> | undefined): number {
  if (!actions || actions.length === 0) return 0;
  return actions.reduce((sum, a) => sum + parseMetricValue(a.value), 0);
}

/**
 * Parses an action array and returns the first matching value (for CTR-like arrays).
 */
export function parseActionArrayFirst(actions: Array<{ action_type: string; value: string }> | undefined): number {
  if (!actions || actions.length === 0) return 0;
  return parseMetricValue(actions[0]!.value);
}

/**
 * Parses the `actions` array into individual action type counts.
 * Returns a map of action_type → count.
 */
export function parseActionsByType(
  actions: Array<{ action_type: string; value: string }> | undefined
): Record<string, number> {
  const result: Record<string, number> = {};
  if (!actions) return result;
  for (const action of actions) {
    result[action.action_type] = (result[action.action_type] ?? 0) + parseMetricValue(action.value);
  }
  return result;
}

/**
 * Parses the `cost_per_action_type` array into individual costs.
 * Returns a map of action_type → cost.
 */
export function parseCostByActionType(
  costPerAction: Array<{ action_type: string; value: string }> | undefined
): Record<string, number> {
  const result: Record<string, number> = {};
  if (!costPerAction) return result;
  for (const entry of costPerAction) {
    result[entry.action_type] = parseMetricValue(entry.value);
  }
  return result;
}

/**
 * Story 35.5 — per-attribution-window action-count parser.
 *
 * Parses the `actions[]` array into a nested map `action_type → window → count`
 * where `window ∈ { default, 1d_click, 7d_click, 28d_click, 1d_view }`.
 *
 * - `default` is the `value` field — Meta populates this with the sum across
 *   the account's `default_attribution_spec` (typically `7d_click+1d_view`
 *   post Jan 12, 2026).
 * - The 4 explicit window fields are the per-window splits Meta returns when
 *   the insights query requests `action_attribution_windows`.
 *
 * `7d_view` and `28d_view` were REMOVED on Jan 12, 2026 — the AdsActionStats
 * typedef still falsely lists them (Meta did not update the typedef; values
 * stopped populating). Read defensively (handled by `parseMetricValue`
 * which treats `undefined` as 0).
 *
 * Returns the empty record when `actions` is `undefined` or empty.
 * If the same action_type appears twice in the array (Meta has been
 * observed to do this on rare server-side aggregations), the per-window
 * counts SUM rather than overwrite — mirrors `parseActionsByType`.
 *
 * Source: developers.facebook.com/docs/marketing-api/reference/ads-action-stats
 * verified 2026-05-19.
 */
export function parseActionsByTypeWithWindows(
  actions: ActionStatsEntry[] | undefined
): Record<string, { default: number; "1d_click": number; "7d_click": number; "28d_click": number; "1d_view": number }> {
  const result: Record<
    string,
    { default: number; "1d_click": number; "7d_click": number; "28d_click": number; "1d_view": number }
  > = {};
  if (!actions) return result;
  for (const action of actions) {
    if (!action || typeof action.action_type !== "string") continue;
    const existing = result[action.action_type] ?? {
      default: 0,
      "1d_click": 0,
      "7d_click": 0,
      "28d_click": 0,
      "1d_view": 0,
    };
    existing.default += parseMetricValue(action.value);
    existing["1d_click"] += parseMetricValue(action["1d_click"]);
    existing["7d_click"] += parseMetricValue(action["7d_click"]);
    existing["28d_click"] += parseMetricValue(action["28d_click"]);
    existing["1d_view"] += parseMetricValue(action["1d_view"]);
    result[action.action_type] = existing;
  }
  return result;
}

/**
 * Story 35.5 — per-attribution-window cost-per-action parser.
 *
 * Parses the `cost_per_action_type[]` array into a nested map
 * `action_type → window → cost`. Same shape as
 * `parseActionsByTypeWithWindows`, but costs DON'T sum on duplicate
 * `action_type` rows — they overwrite (the last entry wins). Meta does
 * not normally return duplicate cost rows; this matches
 * `parseCostByActionType`'s existing behavior on duplicates.
 *
 * NOTE: per-window costs go UP as windows shrink (1d_click costs ~2x the
 * 28d_click cost because the same spend ÷ fewer conversions = higher
 * cost). Adapter consumers MUST treat per-window costs as independent
 * scalars — the cumulative-nesting invariant of counts does NOT apply to
 * costs.
 */
export function parseCostByActionTypeWithWindows(
  costPerAction: ActionStatsEntry[] | undefined
): Record<string, { default: number; "1d_click": number; "7d_click": number; "28d_click": number; "1d_view": number }> {
  const result: Record<
    string,
    { default: number; "1d_click": number; "7d_click": number; "28d_click": number; "1d_view": number }
  > = {};
  if (!costPerAction) return result;
  for (const entry of costPerAction) {
    if (!entry || typeof entry.action_type !== "string") continue;
    result[entry.action_type] = {
      default: parseMetricValue(entry.value),
      "1d_click": parseMetricValue(entry["1d_click"]),
      "7d_click": parseMetricValue(entry["7d_click"]),
      "28d_click": parseMetricValue(entry["28d_click"]),
      "1d_view": parseMetricValue(entry["1d_view"]),
    };
  }
  return result;
}

// ============================================================================
// Helper: v26 Attribution-Setting KV Probe (Story 35.5, AC #6)
// ============================================================================

/** Narrow KV interface used by the attribution-setting probe. */
interface AttributionSettingKv {
  get(key: string): Promise<string | null>;
  put(key: string, value: string, options?: { expirationTtl?: number }): Promise<void>;
}

/**
 * Story 35.5 — read the cached attribution_setting for an integration.
 *
 * Returns `null` on KV miss, KV read failure, or invalid cached value.
 * Callers should re-probe via the `/{adAccountId}?fields=attribution_setting`
 * endpoint on null, then `writeAttributionSetting` to refresh the cache.
 *
 * KV failures are non-fatal — we log and return null so the adapter can
 * fall through to the legacy-safe `configured_at_call_time` branch.
 */
export async function readAttributionSetting(
  ctx: AdapterContext
): Promise<AttributionSetting | null> {
  try {
    const kv = ctx.env.KV as AttributionSettingKv | undefined;
    if (!kv || typeof kv.get !== "function") return null;
    const raw = await kv.get(attributionSettingKvKey(ctx.integrationId));
    if (raw === "use_account_default" || raw === "configured_at_call_time") return raw;
    return null;
  } catch (err) {
    console.log(
      JSON.stringify({
        event: "meta_ads_attribution_setting_kv_read_failed",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        // Truncate to 200 chars — defends against future runtime
        // emitting unbounded provider-controlled strings in error bodies.
        error: (err instanceof Error ? err.message : String(err)).slice(0, 200),
      })
    );
    return null;
  }
}

/**
 * Story 35.5 — persist the attribution_setting probe result to KV.
 *
 * Best-effort — a KV write failure is non-blocking. The adapter will
 * re-probe on the next fetch call. Emits a forensic log event so
 * operators can alert on a partial KV outage that would otherwise
 * burn the project's Marketing API budget via repeated re-probing.
 *
 * TTL: 24h (Meta accounts rarely change attribution_setting mid-day).
 */
export async function writeAttributionSetting(
  ctx: AdapterContext,
  value: AttributionSetting
): Promise<void> {
  try {
    const kv = ctx.env.KV as AttributionSettingKv | undefined;
    if (!kv || typeof kv.put !== "function") return;
    await kv.put(attributionSettingKvKey(ctx.integrationId), value, {
      expirationTtl: ATTRIBUTION_SETTING_TTL_SECONDS,
    });
  } catch (err) {
    console.log(
      JSON.stringify({
        event: "meta_ads_attribution_setting_kv_write_failed",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        attemptedValue: value,
        error: (err instanceof Error ? err.message : String(err)).slice(0, 200),
      })
    );
  }
}

/**
 * Story 35.5 — coerce an arbitrary string into a valid AttributionSetting.
 *
 * Maps Meta's response (`"use_account_default"` / `"configured_at_call_time"`
 * / anything else / undefined) to a known branch. Defaults to
 * `"configured_at_call_time"` on unknown/undefined input — this is the
 * legacy-safe path (pre-v26 accounts that don't expose the field).
 *
 * Round 1 code review (C4): logs unknown future values (e.g. a hypothetical
 * v27 `"hybrid_default"`) so operators can detect Meta surface drift
 * without parsing every probe response themselves. The legacy-safe default
 * still applies — we err on the side of pre-v26 behavior.
 */
export function coerceAttributionSetting(raw: string | null | undefined): AttributionSetting {
  if (raw === "use_account_default" || raw === "configured_at_call_time") return raw;
  if (raw !== undefined && raw !== null && raw !== "") {
    console.log(
      JSON.stringify({
        event: "meta_ads_attribution_setting_unknown_value",
        // Truncate to defend against the very-unlikely case of Meta
        // surfacing a long string value here.
        observedValue: String(raw).slice(0, 64),
      })
    );
  }
  return "configured_at_call_time";
}

// ============================================================================
// Helper: Build per-call action_attribution_windows query fragment (AC #2)
// ============================================================================

/**
 * Story 35.5 — append the right attribution query params to an insights URL.
 *
 * Branch logic per v26 (April 2026) attribution-setting field:
 *   - `"use_account_default"` → append `use_account_attribution_setting=true`
 *     and OMIT `action_attribution_windows` entirely (Meta returns whatever
 *     the account-level setting says).
 *   - `"configured_at_call_time"` → append the explicit 4-window list
 *     (1d_click + 7d_click + 28d_click + 1d_view). `7d_view` + `28d_view`
 *     are intentionally OMITTED — Meta removed them Jan 12, 2026.
 *
 * The function takes a URL string and returns the URL string with the
 * appropriate query suffix appended. Caller is responsible for ensuring
 * the URL already contains a `?` (it does, since `fetchMetrics` builds
 * the insights URL with `?fields=...` first).
 */
export function appendAttributionParams(url: string, setting: AttributionSetting): string {
  // Round 1 code review (H3): defend against callers passing a URL without
  // an existing `?` — use `?` for the first param, `&` for subsequent ones.
  // The two production call-sites (FB+IG fetchMetrics) always pass a URL
  // with `?fields=...` already present, so the `&` branch is the hot path.
  const separator = url.includes("?") ? "&" : "?";
  if (setting === "use_account_default") {
    return `${url}${separator}use_account_attribution_setting=true`;
  }
  const windowsParam = encodeURIComponent(JSON.stringify([...ACTIVE_ATTRIBUTION_WINDOWS]));
  return `${url}${separator}action_attribution_windows=${windowsParam}`;
}

// ============================================================================
// Helper: ASC dynamic_attribution discriminator (Story 35.5, AC #5 + Task 12)
// ============================================================================

/**
 * Story 35.5 — detect Advantage+ Shopping Campaign (ASC) records.
 *
 * ASC's `default_attribution_spec` is `dynamic_attribution` — Meta picks
 * the attribution window per-event. The `action_purchase` metric populates
 * without a deterministic default window, and per-window splits would all
 * return null. The transformer emits `_dynamicAttribution: true` on these
 * records so widget renderers can hide the per-window columns (a 4-NULL
 * row would otherwise show as "no data" and confuse the operator).
 *
 * Detection signal: `optimization_goal === null && objective === "OUTCOME_SALES"`.
 * Only meaningful at campaign-level insights (the account-level rollup
 * aggregates across campaigns of mixed types).
 */
export function isAdvantagePlusShoppingCampaign(row: {
  optimization_goal?: unknown;
  objective?: unknown;
}): boolean {
  return row.optimization_goal === null && row.objective === "OUTCOME_SALES";
}

// ============================================================================
// Helper: per-window record field emission (Story 35.5, AC #4 + Task 5)
// ============================================================================

/**
 * Story 35.5 — emit 56 per-window record fields onto a record object.
 *
 * Mutates `record` in place. Adds:
 *   - 24 action-count per-window fields (6 action types × 4 windows)
 *   - 8 omni-variant per-window fields (2 omni types × 4 windows)
 *   - 24 cost-per-action per-window fields (6 action types × 4 windows)
 *
 * `actionsByWindow` is the output of `parseActionsByTypeWithWindows(row.actions)`.
 * `costByWindow` is the output of `parseCostByActionTypeWithWindows(row.cost_per_action_type)`.
 *
 * Per AC #5 the cost-per-window fields are NOT registered in `FB_ADS_METRICS`
 * (kept off the public picker palette to avoid bloat), but they ARE emitted
 * here so the record map contains them for deep-dive consumers.
 */
export function emitPerWindowRecordFields(
  record: Record<string, unknown>,
  actionsByWindow: ReturnType<typeof parseActionsByTypeWithWindows>,
  costByWindow: ReturnType<typeof parseCostByActionTypeWithWindows>
): void {
  // Action counts (24 = 6 action types × 4 windows)
  for (const { keyPrefix, wireType } of PER_WINDOW_ACTION_TYPES) {
    const windowCounts = actionsByWindow[wireType];
    for (const window of ACTIVE_ATTRIBUTION_WINDOWS) {
      record[`${keyPrefix}_${window}`] = windowCounts ? windowCounts[window] : 0;
    }
  }
  // Omni-variant action counts (8 = 2 omni types × 4 windows)
  for (const { keyPrefix, wireType } of PER_WINDOW_OMNI_ACTION_TYPES) {
    const windowCounts = actionsByWindow[wireType];
    for (const window of ACTIVE_ATTRIBUTION_WINDOWS) {
      record[`${keyPrefix}_${window}`] = windowCounts ? windowCounts[window] : 0;
    }
  }
  // Cost-per-action per-window (24 = 6 action types × 4 windows; emitted to
  // records but NOT in FB_ADS_METRICS — deep-dive data only).
  for (const { keyPrefix, wireType } of PER_WINDOW_ACTION_TYPES) {
    const windowCosts = costByWindow[wireType];
    for (const window of ACTIVE_ATTRIBUTION_WINDOWS) {
      record[`cost_per_${keyPrefix}_${window}`] = windowCosts ? windowCosts[window] : 0;
    }
  }
}

// ============================================================================
// Helper: ensure attribution_setting (KV-cached or re-probe)
// ============================================================================

/**
 * Story 35.5 — resolve the attribution_setting for an integration's ad account.
 *
 * Reads the cached value from KV first. On miss (or KV failure), re-probes
 * the `/{adAccountId}?fields=attribution_setting` endpoint and persists
 * the result. Probe failures are non-fatal — the function returns the
 * legacy-safe `"configured_at_call_time"` branch (matching pre-v26
 * behavior so existing customers don't see attribution-window changes
 * mid-flight from a transient probe failure) AND caches that default to
 * KV so subsequent fetchMetrics calls don't re-probe on every fetch.
 *
 * Token usage:
 *   - KV cache hit: 0 Marketing API reads
 *   - KV cache miss + probe success: 1 Marketing API read + 1 KV write
 *   - KV cache miss + probe failure: 1 Marketing API read (failed) + 1 KV write
 *     (caches legacy default — see R2-C2 fix)
 *   - KV not configured (env.KV missing get/put): 0 Marketing API reads
 *     and 0 KV writes (short-circuit to legacy default)
 */
export async function ensureAttributionSetting(
  ctx: AdapterContext,
  adAccountId: string,
  authHeaders: { headers: { Authorization: string } }
): Promise<AttributionSetting> {
  const cached = await readAttributionSetting(ctx);
  if (cached) return cached;

  // If the env.KV binding doesn't expose a usable `get`/`put` API (test
  // doubles that pass `{}` as the KV binding, dev environments without
  // KV configured, etc.), skip the probe entirely and return the
  // legacy-safe default. Probing without a way to cache the result would
  // burn 1 Marketing API call per fetchMetrics — wasteful and fixable
  // by configuring KV upstream.
  const kv = ctx.env.KV as { get?: unknown; put?: unknown } | undefined;
  if (!kv || typeof kv.get !== "function" || typeof kv.put !== "function") {
    return "configured_at_call_time";
  }

  // Cache miss — probe the ad account. Best-effort; on any failure
  // return the legacy-safe default.
  const probeUrl = `${META_GRAPH_BASE}/${adAccountId}?fields=attribution_setting`;
  const probeResult = await safeFetch(probeUrl, authHeaders, 5_000);
  if (!probeResult.success) {
    console.log(
      JSON.stringify({
        event: "meta_ads_attribution_setting_probe_failed",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        adAccountId,
        // Truncate sanitized provider error to 200 chars.
        error: (probeResult.error.message ?? "unknown").slice(0, 200),
      })
    );
    // Round 2 code review (R2-C2): cache the legacy default so subsequent
    // fetchMetrics calls don't re-probe on every fetch when the probe
    // endpoint is transiently failing. The self-heal flow on subcode 2655
    // is the recovery path if Meta later complains about our query shape.
    await writeAttributionSetting(ctx, "configured_at_call_time");
    return "configured_at_call_time";
  }

  try {
    const body = (await probeResult.data.json()) as { attribution_setting?: string };
    const setting = coerceAttributionSetting(body.attribution_setting);
    await writeAttributionSetting(ctx, setting);
    return setting;
  } catch {
    // Probe responded but the body is unparseable — same caching strategy.
    await writeAttributionSetting(ctx, "configured_at_call_time");
    return "configured_at_call_time";
  }
}

// ============================================================================
// Helper: Date Range Construction (ISO dates, NOT Unix timestamps)
// ============================================================================

/**
 * Builds time_range parameter for Marketing API.
 * Clamps start to max 37 months ago. Uses ISO date strings.
 * `until` IS inclusive (unlike Page Insights where `until` is non-inclusive).
 */
export function buildTimeRange(start: Date, end: Date): { timeRange: string; clamped: boolean } {
  const maxRetention = new Date();
  maxRetention.setUTCMonth(maxRetention.getUTCMonth() - MAX_RETENTION_MONTHS);

  let clamped = false;
  let effectiveStart = start;
  if (start < maxRetention) {
    effectiveStart = maxRetention;
    clamped = true;
  }

  const since = effectiveStart.toISOString().split("T")[0];
  const until = end.toISOString().split("T")[0];

  return {
    timeRange: JSON.stringify({ since, until }),
    clamped,
  };
}

// ============================================================================
// Metric Definitions (Subtask 1.2)
// ============================================================================

export const FB_ADS_METRICS: MetricDefinition[] = [
  // ── Delivery (8) ──
  { key: "impressions", label: "Impressions", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "reach", label: "Reach", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "frequency", label: "Frequency", type: "number", category: "Delivery", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { numerator: "impressions", denominator: "reach" }, compatibleWidgets: ["kpi", "line-chart"] },
  { key: "spend", label: "Spend", type: "currency", category: "Spend", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "cpm", label: "CPM", type: "currency", category: "Efficiency", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "impressions" }, compatibleWidgets: ["kpi", "line-chart"] },
  { key: "cpp", label: "CPP (Cost per 1K Reach)", type: "currency", category: "Efficiency", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "reach" }, compatibleWidgets: ["kpi"] },
  { key: "full_view_impressions", label: "Full View Impressions", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "social_spend", label: "Social Spend", type: "currency", category: "Spend", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi"] },

  // ── Clicks (10) ──
  { key: "clicks", label: "Clicks (All)", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "link_clicks", label: "Link Clicks", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "ctr", label: "CTR", type: "percentage", category: "Performance", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "clicks", denominator: "impressions" }, compatibleWidgets: ["kpi", "line-chart", "gauge"] },
  { key: "cpc", label: "CPC", type: "currency", category: "Efficiency", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "clicks" }, compatibleWidgets: ["kpi", "line-chart"] },
  { key: "inline_link_click_ctr", label: "Link Click CTR", type: "percentage", category: "Performance", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "link_clicks", denominator: "impressions" }, compatibleWidgets: ["kpi", "gauge"] },
  { key: "outbound_clicks", label: "Outbound Clicks", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "outbound_clicks_ctr", label: "Outbound CTR", type: "percentage", category: "Performance", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "outbound_clicks", denominator: "impressions" }, compatibleWidgets: ["kpi"] },
  { key: "unique_clicks", label: "Unique Clicks", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "unique_ctr", label: "Unique CTR", type: "percentage", category: "Performance", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "unique_clicks", denominator: "reach" }, compatibleWidgets: ["kpi"] },
  { key: "cost_per_unique_click", label: "Cost per Unique Click", type: "currency", category: "Efficiency", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "unique_clicks" }, compatibleWidgets: ["kpi"] },
  { key: "cost_per_outbound_click", label: "Cost per Outbound Click", type: "currency", category: "Efficiency", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "outbound_clicks" }, compatibleWidgets: ["kpi"] },

  // ── Conversions & Actions (14) ──
  { key: "conversions", label: "Conversions", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart", "funnel"] },
  { key: "conversion_rate", label: "Conversion Rate", type: "percentage", category: "Performance", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "conversions", denominator: "clicks" }, compatibleWidgets: ["kpi", "gauge"] },
  { key: "cost_per_conversion", label: "Cost per Conversion", type: "currency", category: "Efficiency", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "conversions" }, compatibleWidgets: ["kpi", "line-chart"] },
  { key: "purchase_value", label: "Purchase Revenue", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "purchase_roas", label: "ROAS", type: "number", category: "Revenue", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { numerator: "purchase_value", denominator: "spend" }, compatibleWidgets: ["kpi", "line-chart", "gauge"] },
  { key: "website_purchase_roas", label: "Website ROAS", type: "number", category: "Revenue", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { denominator: "spend" }, compatibleWidgets: ["kpi", "gauge"] },
  { key: "cost_per_result", label: "Cost per Result", type: "currency", category: "Efficiency", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "results" }, compatibleWidgets: ["kpi"] },
  { key: "results", label: "Results", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "result_rate", label: "Result Rate", type: "percentage", category: "Performance", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "results", denominator: "impressions" }, compatibleWidgets: ["kpi"] },
  { key: "landing_page_views", label: "Landing Page Views", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "landing_page_view_per_link_click", label: "LP View / Link Click", type: "percentage", category: "Performance", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "landing_page_views", denominator: "link_clicks" }, compatibleWidgets: ["kpi"] },

  // ── Key Action Types (15) — parsed from actions[] array ──
  { key: "action_link_click", label: "Link Click Actions", type: "number", category: "Actions", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "action_post_engagement", label: "Post Engagement Actions", type: "number", category: "Actions", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "action_page_engagement", label: "Page Engagement Actions", type: "number", category: "Actions", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "action_video_view", label: "Video View Actions", type: "number", category: "Actions", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "action_landing_page_view", label: "Landing Page View Actions", type: "number", category: "Actions", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "action_purchase", label: "Website Purchases", type: "number", category: "Actions", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "action_lead", label: "Website Leads", type: "number", category: "Actions", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "action_add_to_cart", label: "Add to Cart", type: "number", category: "Actions", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "funnel"] },
  { key: "action_initiate_checkout", label: "Checkout Started", type: "number", category: "Actions", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "funnel"] },
  { key: "action_complete_registration", label: "Registrations", type: "number", category: "Actions", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "action_view_content", label: "Content Views", type: "number", category: "Actions", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "funnel"] },
  { key: "action_omni_purchase", label: "Cross-channel Purchases", type: "number", category: "Actions", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "action_all_leads", label: "All-surface Leads", type: "number", category: "Actions", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "action_app_install", label: "App Installs", type: "number", category: "Actions", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "action_flow_complete", label: "Lead Form Completions", type: "number", category: "Actions", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },

  // ── Cost per Action Type (3) — parsed from cost_per_action_type[] array ──
  { key: "cost_per_action_link_click", label: "Cost per Link Click", type: "currency", category: "Efficiency", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "action_link_click" }, compatibleWidgets: ["kpi"] },
  { key: "cost_per_action_purchase", label: "Cost per Purchase", type: "currency", category: "Efficiency", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "action_purchase" }, compatibleWidgets: ["kpi"] },
  { key: "cost_per_action_lead", label: "Cost per Lead", type: "currency", category: "Efficiency", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "action_lead" }, compatibleWidgets: ["kpi"] },

  // ── Video (12) ──
  { key: "video_thruplay", label: "Video ThruPlays", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "video_thruplay_rate", label: "ThruPlay Rate", type: "percentage", category: "Video", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "video_thruplay", denominator: "impressions" }, compatibleWidgets: ["kpi", "gauge"] },
  { key: "cost_per_thruplay", label: "Cost per ThruPlay", type: "currency", category: "Video", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "video_thruplay" }, compatibleWidgets: ["kpi"] },
  { key: "cost_per_15_sec_video_view", label: "Cost per 15s View", type: "currency", category: "Video", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "video_15s" }, compatibleWidgets: ["kpi"] },
  { key: "cost_per_2_sec_continuous_video_view", label: "Cost per 2s View", type: "currency", category: "Video", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "video_2s_continuous" }, compatibleWidgets: ["kpi"] },
  { key: "video_p25", label: "Video 25%", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "funnel"] },
  { key: "video_p50", label: "Video 50%", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "funnel"] },
  { key: "video_p75", label: "Video 75%", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "funnel"] },
  { key: "video_p100", label: "Video 100%", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "funnel"] },
  { key: "video_2s_continuous", label: "2s Continuous Views", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "video_15s", label: "15s Views", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "video_30s", label: "30s Views", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "video_avg_time", label: "Avg Watch Time", type: "duration", category: "Video", dataType: "Duration", aggregationKind: "ratio", ratioComponents: { denominator: "video_plays" }, compatibleWidgets: ["kpi"] },
  { key: "video_plays", label: "Video Plays", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },

  // ── Quality / Relevance (3) ──
  // Note: Values are enum strings (ABOVE_AVERAGE, AVERAGE, BELOW_AVERAGE_10/20/35).
  // type:"number" is the least-wrong fit — MetricDefinition has no "string" variant.
  // Widget renderers MUST check dataType:"Enum" and display raw string values, not format as number.
  { key: "quality_ranking", label: "Quality Ranking", type: "number", category: "Quality", dataType: "Enum", compatibleWidgets: ["data-table"] },
  { key: "engagement_rate_ranking", label: "Engagement Rate Ranking", type: "number", category: "Quality", dataType: "Enum", compatibleWidgets: ["data-table"] },
  { key: "conversion_rate_ranking", label: "Conversion Rate Ranking", type: "number", category: "Quality", dataType: "Enum", compatibleWidgets: ["data-table"] },

  // ── Engagement (2) ──
  { key: "inline_post_engagement", label: "Post Engagement", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "cost_per_inline_post_engagement", label: "Cost per Post Engagement", type: "currency", category: "Efficiency", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "inline_post_engagement" }, compatibleWidgets: ["kpi"] },

  // ── Story 35.5: Per-Attribution-Window Action Counts (24 = 6 action types × 4 windows) ──
  // Each per-window MetricDefinition carries the `attributionWindow` discriminator so widget
  // renderers can group these into a "Choose attribution window" sub-menu rather than
  // surfacing 24 sibling rows. The cumulative-nesting invariant of Meta's windows
  // (28d_click ⊃ 7d_click ⊃ 1d_click) is a downstream renderer concern — the raw values
  // are what the API returns.
  ...PER_WINDOW_ACTION_TYPES.flatMap(({ keyPrefix, label }) =>
    ACTIVE_ATTRIBUTION_WINDOWS.map((window): MetricDefinition => ({
      key: `${keyPrefix}_${window}`,
      label: `${label} (${ATTRIBUTION_WINDOW_LABEL_SUFFIX[window]})`,
      type: "number",
      category: "Actions",
      dataType: "Integer",
      // Story 31-31 — annotated AT THE GENERATOR (AC #4): every per-window
      // variant is an attributed action COUNT and inherits the base metric's
      // additive kind. No window-specific ratio metrics are generated, so no
      // window-variant ratioComponents mapping is needed.
      aggregationKind: "additive",
      compatibleWidgets: ["kpi", "line-chart"],
      attributionWindow: window,
    }))
  ),

  // ── Story 35.5: Omni-Variant Per-Window Action Counts (8 = 2 omni types × 4 windows) ──
  ...PER_WINDOW_OMNI_ACTION_TYPES.flatMap(({ keyPrefix, label }) =>
    ACTIVE_ATTRIBUTION_WINDOWS.map((window): MetricDefinition => ({
      key: `${keyPrefix}_${window}`,
      label: `${label} (${ATTRIBUTION_WINDOW_LABEL_SUFFIX[window]})`,
      type: "number",
      category: "Actions",
      dataType: "Integer",
      // Story 31-31 — additive per-window action counts (see comment above).
      aggregationKind: "additive",
      compatibleWidgets: ["kpi", "line-chart"],
      attributionWindow: window,
    }))
  ),

  // NOTE (Story 35.5): cost-per-action × window fields (cost_per_action_purchase_7d_click etc.)
  // are emitted on record bodies (see fetchMetrics) but NOT registered here — they're deep-dive
  // data exposed via the record map, not the public metric picker palette. Future Story 35.5.2
  // can promote these into MetricDefinitions if customer demand emerges. Keeping FB_ADS_METRICS
  // under the 100-entry sanity cap is intentional — see Risk & Mitigation in 35-5 story file.
];

// ============================================================================
// Dimension Definitions (Subtask 1.3)
// ============================================================================

export const FB_ADS_DIMENSIONS: DimensionDefinition[] = [
  // ── Time (1) ──
  { key: "date", label: "Date", type: "Time", exampleValues: ["2026-03-01", "2026-03-02"] },
  // ── Campaign Structure (3) ──
  { key: "campaign_name", label: "Campaign", type: "Structure", exampleValues: ["Summer Sale", "Brand Awareness"] },
  { key: "adset_name", label: "Ad Set", type: "Structure", exampleValues: ["18-24 Female", "Lookalike 1%"] },
  { key: "ad_name", label: "Ad", type: "Structure", exampleValues: ["Carousel Creative A"] },
  // ── Demographics (2) ──
  { key: "age", label: "Age", type: "Demo", exampleValues: ["18-24", "25-34", "35-44"] },
  { key: "gender", label: "Gender", type: "Demo", exampleValues: ["male", "female"] },
  // ── Geography (3) ──
  { key: "country", label: "Country", type: "Geo", exampleValues: ["US", "GB", "CA"] },
  { key: "region", label: "Region", type: "Geo", exampleValues: ["California", "Texas", "New York"] },
  { key: "dma", label: "DMA", type: "Geo", exampleValues: ["New York", "Los Angeles", "Chicago"] },
  // ── Device & Platform (4) ──
  { key: "device_platform", label: "Device", type: "Tech", exampleValues: ["mobile", "desktop"] },
  { key: "publisher_platform", label: "Platform", type: "Delivery", exampleValues: ["facebook", "instagram", "audience_network"] },
  { key: "placement", label: "Placement", type: "Delivery", exampleValues: ["feed", "stories", "reels"] },
  { key: "impression_device", label: "Impression Device", type: "Tech", exampleValues: ["mobile", "desktop", "tablet"] },
  // ── Product / Catalog (4) ──
  { key: "product_id", label: "Product ID", type: "Product", exampleValues: ["SKU-12345", "SKU-67890"] },
  { key: "product_brand_breakdown", label: "Product Brand", type: "Product", exampleValues: ["Nike", "Adidas"] },
  { key: "product_category_breakdown", label: "Product Category", type: "Product", exampleValues: ["Shoes", "Apparel"] },
  { key: "product_custom_label_0_breakdown", label: "Custom Label 0", type: "Product", exampleValues: ["Bestseller", "New Arrival"] },
  { key: "product_custom_label_1_breakdown", label: "Custom Label 1", type: "Product", exampleValues: ["Summer", "Winter"] },
  { key: "product_custom_label_2_breakdown", label: "Custom Label 2", type: "Product", exampleValues: ["Sale", "Full Price"] },
  { key: "product_custom_label_3_breakdown", label: "Custom Label 3", type: "Product", exampleValues: ["Priority", "Standard"] },
  { key: "product_custom_label_4_breakdown", label: "Custom Label 4", type: "Product", exampleValues: ["Margin-High", "Margin-Low"] },
  // ── Creative Asset (5) ──
  { key: "image_asset", label: "Image Asset", type: "Creative", exampleValues: ["image_1", "image_2"] },
  { key: "video_asset", label: "Video Asset", type: "Creative", exampleValues: ["video_1", "video_2"] },
  { key: "body_asset", label: "Body Asset", type: "Creative", exampleValues: ["copy_1", "copy_2"] },
  { key: "title_asset", label: "Title Asset", type: "Creative", exampleValues: ["headline_1", "headline_2"] },
  { key: "call_to_action_asset", label: "CTA Asset", type: "Creative", exampleValues: ["Shop Now", "Learn More"] },
  // ── Action Breakdowns (4) ──
  { key: "action_type", label: "Action Type", type: "Action", exampleValues: ["link_click", "purchase", "lead"] },
  { key: "action_device", label: "Action Device", type: "Action", exampleValues: ["desktop", "iphone", "android_smartphone"] },
  { key: "action_destination", label: "Action Destination", type: "Action", exampleValues: ["website", "app", "messenger"] },
  { key: "conversion_destination", label: "Conversion Destination", type: "Action", exampleValues: ["website", "app", "offline"] },
];

// ============================================================================
// Insights Fields (for the API call)
// ============================================================================

const ACCOUNT_INSIGHTS_FIELDS = [
  // Delivery
  "impressions", "reach", "frequency", "spend", "cpm", "cpp",
  "full_view_impressions", "social_spend",
  // Clicks
  "clicks", "ctr", "cpc", "inline_link_clicks", "inline_link_click_ctr",
  "outbound_clicks", "outbound_clicks_ctr", "cost_per_outbound_click",
  "unique_clicks", "unique_ctr", "cost_per_unique_click",
  // Conversions & Actions
  "actions", "action_values", "cost_per_action_type",
  "purchase_roas", "website_purchase_roas",
  // Video
  "video_thru_play_actions", "cost_per_thruplay",
  "cost_per_15_sec_video_view", "cost_per_2_sec_continuous_video_view",
  "video_p25_watched_actions", "video_p50_watched_actions",
  "video_p75_watched_actions", "video_p100_watched_actions",
  "video_continuous_2_sec_watched_actions",
  "video_15_sec_watched_actions", "video_30_sec_watched_actions",
  "video_avg_time_watched_actions", "video_play_actions",
  // Quality
  "quality_ranking", "engagement_rate_ranking", "conversion_rate_ranking",
  // Engagement
  "inline_post_engagement", "cost_per_inline_post_engagement",
].join(",");

const CAMPAIGN_INSIGHTS_FIELDS = [
  "campaign_name", "campaign_id",
  "impressions", "reach", "clicks", "spend",
  "actions", "action_values", "cost_per_action_type",
  "quality_ranking", "engagement_rate_ranking", "conversion_rate_ranking",
  // Story 35.5 — ASC discriminator inputs (optimization_goal === null &&
  // objective === "OUTCOME_SALES" → Advantage+ Shopping Campaign). These
  // are technically campaign-object fields (not insights aggregates) but
  // Meta's insights endpoint surfaces them as passthrough when requested.
  "optimization_goal", "objective",
].join(",");

/**
 * Story 35.5 — feature flag for campaign-level per-window field emission.
 * Account-level dailySummary records ALWAYS emit per-window fields.
 * Campaign-level records emit per-window fields when this flag is true;
 * disabling here is a quick lever if the record-size impact on KV/D1
 * storage becomes a concern. Default: true (per AC #13).
 */
export const ENABLE_PER_WINDOW_CAMPAIGN_METRICS = true;

// ============================================================================
// Facebook Ads Adapter (Subtask 1.1)
// ============================================================================

export const facebookAdsAdapter: IntegrationAdapter = {
  platform: "facebook-ads",

  rateLimits: {
    requestsPerMinute: 60,
    requestsPerDay: 86400,
  },

  // --------------------------------------------------------------------------
  // fetchMetrics (Subtask 1.4)
  // --------------------------------------------------------------------------
  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    const adAccountId = ctx.accountId;
    if (!adAccountId) {
      return adapterErr({
        code: "account_not_found",
        message: "No Ad Account selected. Please select an Ad Account first.",
        retryable: false,
      });
    }

    const token = credentials.accessToken;
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    // Build time range (ISO dates, clamped to 37 months)
    const { timeRange, clamped } = buildTimeRange(dateRange.start, dateRange.end);

    if (clamped) {
      console.log(JSON.stringify({
        event: "fb_ads_date_clamped",
        adAccountId,
        tenantId: ctx.tenantId,
        originalStart: dateRange.start.toISOString(),
      }));
    }

    let rateLimitWarning = false;

    // Story 35.5 — resolve attribution_setting branch.
    // Cache hit costs 0 Marketing API reads; cache miss costs 1 read.
    // On any failure the resolver returns "configured_at_call_time"
    // (the legacy-safe pre-v26 path), so we always have a valid branch.
    let attributionSetting = await ensureAttributionSetting(ctx, adAccountId, authHeaders);

    // --- Fetch account-level insights ---
    // Story 35.5 — the URL carries either `action_attribution_windows=[…]`
    // (configured_at_call_time branch) or `use_account_attribution_setting=true`
    // (use_account_default branch). On subcode 2655 ("attribution-settings
    // mismatch") we self-heal by updating the KV cache to use_account_default
    // and retrying once.
    const buildAccountUrl = (setting: AttributionSetting): string => {
      const base = `${META_GRAPH_BASE}/${adAccountId}/insights?fields=${ACCOUNT_INSIGHTS_FIELDS}&time_range=${encodeURIComponent(timeRange)}&time_increment=1&level=account`;
      return appendAttributionParams(base, setting);
    };

    let accountUrl = buildAccountUrl(attributionSetting);
    let accountResult = await safeFetch(accountUrl, authHeaders, 25_000);

    // Self-heal: if subcode 2655 ("attribution-settings mismatch") and we
    // were on the configured_at_call_time branch, flip the KV cache to
    // use_account_default and retry exactly once.
    if (
      !accountResult.success &&
      attributionSetting === "configured_at_call_time" &&
      accountResult.error.providerMessage
    ) {
      const parsedErr = parseMetaGraphError(accountResult.error.providerMessage);
      if (parsedErr?.errorSubcode === META_ATTRIBUTION_SETTING_MISMATCH_SUBCODE) {
        const originalSetting = attributionSetting;
        attributionSetting = "use_account_default";
        await writeAttributionSetting(ctx, attributionSetting);
        console.log(
          JSON.stringify({
            event: "meta_ads_attribution_setting_self_heal",
            adAccountId,
            tenantId: ctx.tenantId,
            integrationId: ctx.integrationId,
            originalSetting,
            correctedSetting: attributionSetting,
          })
        );
        accountUrl = buildAccountUrl(attributionSetting);
        accountResult = await safeFetch(accountUrl, authHeaders, 25_000);
      }
    }

    if (!accountResult.success) {
      return handleMetaFetchError(accountResult);
    }

    const accountResponse = accountResult.data;
    const appUsage = parseAppUsage(accountResponse);
    if (appUsage > RATE_LIMIT_THRESHOLD) {
      rateLimitWarning = true;
      console.log(JSON.stringify({
        event: "meta_rate_limit_approaching",
        callCount: appUsage,
        adAccountId,
        tenantId: ctx.tenantId,
      }));
    }

    let accountData: MarketingInsightsResponse;
    try {
      accountData = (await accountResponse.json()) as MarketingInsightsResponse;
    } catch (err) {
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(
          `Failed to parse Marketing API response: ${err instanceof Error ? err.message : String(err)}`
        ),
        retryable: false,
      });
    }

    // Check for API-level error in the JSON body
    if (accountData.error) {
      const metaError = parseMetaGraphError(JSON.stringify(accountData));

      // Story 35.5 — subcode 2655 self-heal also from JSON body error
      // (defense in depth — Meta has been observed to return 200 with
      // error body on rare edge cases). Same one-shot retry policy.
      if (
        metaError?.errorSubcode === META_ATTRIBUTION_SETTING_MISMATCH_SUBCODE &&
        attributionSetting === "configured_at_call_time"
      ) {
        const originalSetting = attributionSetting;
        attributionSetting = "use_account_default";
        await writeAttributionSetting(ctx, attributionSetting);
        console.log(
          JSON.stringify({
            event: "meta_ads_attribution_setting_self_heal",
            adAccountId,
            tenantId: ctx.tenantId,
            integrationId: ctx.integrationId,
            originalSetting,
            correctedSetting: attributionSetting,
            source: "json_body",
          })
        );
        const retryUrl = buildAccountUrl(attributionSetting);
        const retryResult = await safeFetch(retryUrl, authHeaders, 25_000);
        if (!retryResult.success) return handleMetaFetchError(retryResult);
        try {
          accountData = (await retryResult.data.json()) as MarketingInsightsResponse;
        } catch (err) {
          return adapterErr({
            code: "invalid_response",
            message: sanitizeErrorMessage(
              `Failed to parse Marketing API retry response: ${err instanceof Error ? err.message : String(err)}`
            ),
            retryable: false,
          });
        }
        if (accountData.error) {
          // Retry also failed — emit retryable=false per AC #10 final clause.
          // Use `invalid_request` since the mismatch is between the adapter's
          // request shape and the account's v26 attribution_setting (i.e.
          // the request can never succeed in its current form without the
          // KV self-heal that already failed).
          return adapterErr({
            code: "invalid_request",
            message: "Attribution settings mismatch; unable to fetch action breakdown matrix",
            retryable: false,
          });
        }
      } else if (metaError) {
        return adapterErr({
          code: metaError.code,
          message: sanitizeErrorMessage(metaError.message),
          retryable: metaError.retryable,
        });
      } else {
        return adapterErr({
          code: "invalid_response",
          message: sanitizeErrorMessage(accountData.error.message),
          retryable: false,
        });
      }
    }

    // Get currency from ad account (fetch account info)
    let currency = "USD";
    if (!rateLimitWarning) {
      const accountInfoUrl = `${META_GRAPH_BASE}/${adAccountId}?fields=currency`;
      const infoResult = await safeFetch(accountInfoUrl, authHeaders, 5_000);
      if (infoResult.success) {
        const infoAppUsage = parseAppUsage(infoResult.data);
        if (infoAppUsage > RATE_LIMIT_THRESHOLD) {
          rateLimitWarning = true;
        }
        try {
          const infoBody = (await infoResult.data.json()) as { currency?: string };
          if (infoBody.currency) {
            currency = infoBody.currency;
          }
        } catch {
          // Non-fatal — use default currency
        }
      }
    }

    // Transform account-level insights to daily summary records
    const records: Record<string, unknown>[] = [];

    // Handle pagination for account insights
    let accountPageData = accountData;
    let accountPageCount = 0;
    while (accountPageData && accountPageCount < MAX_PAGES) {
      for (const row of accountPageData.data ?? []) {
        // ── Delivery ──
        const impressions = parseMetricValue(row.impressions);
        const reach = parseMetricValue(row.reach);
        const frequency = parseMetricValue(row.frequency);
        const spend = parseMetricValue(row.spend);
        const cpm = parseMetricValue(row.cpm);
        const cpp = parseMetricValue(row.cpp);
        const fullViewImpressions = parseMetricValue(row.full_view_impressions);
        const socialSpend = parseMetricValue(row.social_spend);

        // ── Clicks ──
        const clicks = parseMetricValue(row.clicks);
        const linkClicks = parseMetricValue(row.inline_link_clicks);
        const ctr = parseMetricValue(row.ctr);
        const cpc = parseMetricValue(row.cpc);
        const inlineLinkClickCtr = parseMetricValue(row.inline_link_click_ctr);
        const outboundClicks = parseActionArrayTotal(row.outbound_clicks);
        const outboundClicksCtr = parseActionArrayFirst(row.outbound_clicks_ctr);
        const costPerOutboundClick = parseActionArrayFirst(row.cost_per_outbound_click);
        const uniqueClicks = parseMetricValue(row.unique_clicks);
        const uniqueCtr = parseMetricValue(row.unique_ctr);
        const costPerUniqueClick = parseMetricValue(row.cost_per_unique_click);

        // ── Conversions ──
        const { totalConversions } = parseActions(row.actions);
        const purchaseValue = parseActionValues(row.action_values);
        const costPerConversion = parseCostPerAction(row.cost_per_action_type);
        const purchaseRoas = parsePurchaseRoas(row.purchase_roas);
        const websitePurchaseRoas = parsePurchaseRoas(row.website_purchase_roas);

        // ── Action type breakdowns ──
        const actionsByType = parseActionsByType(row.actions);
        const costByType = parseCostByActionType(row.cost_per_action_type);

        // Story 35.5 — per-attribution-window breakdowns. Used below by
        // emitPerWindowRecordFields() to add 56 new fields to the daily
        // summary record (24 action counts + 8 omni-variant counts + 24
        // cost-per-action-per-window). The default-window aliases above
        // (action_purchase, action_lead, …) remain unchanged — Story 35.5
        // is additive on the record surface.
        const actionsByWindow = parseActionsByTypeWithWindows(row.actions);
        const costByWindow = parseCostByActionTypeWithWindows(row.cost_per_action_type);

        // ── Video ──
        const videoThruplay = parseVideoThruplay(row.video_thru_play_actions);
        const costPerThruplay = parseMetricValue(row.cost_per_thruplay);
        const costPer15sVideoView = parseActionArrayFirst(row.cost_per_15_sec_video_view);
        const costPer2sContinuousVideoView = parseActionArrayFirst(row.cost_per_2_sec_continuous_video_view);
        const videoP25 = parseActionArrayTotal(row.video_p25_watched_actions);
        const videoP50 = parseActionArrayTotal(row.video_p50_watched_actions);
        const videoP75 = parseActionArrayTotal(row.video_p75_watched_actions);
        const videoP100 = parseActionArrayTotal(row.video_p100_watched_actions);
        const video2sContinuous = parseActionArrayTotal(row.video_continuous_2_sec_watched_actions);
        const video15s = parseActionArrayTotal(row.video_15_sec_watched_actions);
        const video30s = parseActionArrayTotal(row.video_30_sec_watched_actions);
        const videoAvgTime = parseActionArrayTotal(row.video_avg_time_watched_actions);
        const videoPlays = parseActionArrayTotal(row.video_play_actions);

        // ── Engagement ──
        const inlinePostEngagement = parseMetricValue(row.inline_post_engagement);
        const costPerInlinePostEngagement = parseMetricValue(row.cost_per_inline_post_engagement);

        // ── Quality rankings (strings — pass through as-is) ──
        const qualityRanking = row.quality_ranking ?? "";
        const engagementRateRanking = row.engagement_rate_ranking ?? "";
        const conversionRateRanking = row.conversion_rate_ranking ?? "";

        // ── Derived metrics ──
        const conversionRate = clicks > 0 ? (totalConversions / clicks) * 100 : 0;
        const videoThruplayRate = impressions > 0 ? (videoThruplay / impressions) * 100 : 0;
        const effectiveRoas = purchaseRoas > 0 ? purchaseRoas : (spend > 0 ? purchaseValue / spend : 0);
        const landingPageViews = actionsByType["landing_page_view"] ?? 0;
        const landingPageViewPerLinkClick = linkClicks > 0
          ? Math.round((landingPageViews / linkClicks) * 10000) / 100
          : 0;
        // results = totalConversions, result_rate = conversionRate, cost_per_result = cost_per_conversion (Meta's unified result metrics)
        const resultRate = impressions > 0 ? (totalConversions / impressions) * 100 : 0;

        const dailyRecord: Record<string, unknown> = {
          _reportType: "dailySummary",
          date: row.date_start,
          // Delivery
          impressions, reach, frequency, spend, cpm, cpp,
          full_view_impressions: fullViewImpressions,
          social_spend: socialSpend,
          // Clicks
          clicks, link_clicks: linkClicks, ctr, cpc,
          inline_link_click_ctr: inlineLinkClickCtr,
          outbound_clicks: outboundClicks,
          outbound_clicks_ctr: outboundClicksCtr,
          cost_per_outbound_click: costPerOutboundClick,
          unique_clicks: uniqueClicks,
          unique_ctr: uniqueCtr,
          cost_per_unique_click: costPerUniqueClick,
          // Conversions
          conversions: totalConversions,
          conversion_rate: conversionRate,
          cost_per_conversion: costPerConversion > 0 ? costPerConversion : (totalConversions > 0 ? spend / totalConversions : 0),
          purchase_value: purchaseValue,
          purchase_roas: effectiveRoas,
          website_purchase_roas: websitePurchaseRoas,
          cost_per_result: costPerConversion > 0 ? costPerConversion : (totalConversions > 0 ? spend / totalConversions : 0),
          results: totalConversions,
          result_rate: resultRate,
          landing_page_views: landingPageViews,
          landing_page_view_per_link_click: landingPageViewPerLinkClick,
          // Action type breakdowns
          action_link_click: actionsByType["link_click"] ?? 0,
          action_post_engagement: actionsByType["post_engagement"] ?? 0,
          action_page_engagement: actionsByType["page_engagement"] ?? 0,
          action_video_view: actionsByType["video_view"] ?? 0,
          action_landing_page_view: actionsByType["landing_page_view"] ?? 0,
          action_purchase: actionsByType["offsite_conversion.fb_pixel_purchase"] ?? 0,
          action_lead: actionsByType["offsite_conversion.fb_pixel_lead"] ?? 0,
          action_add_to_cart: actionsByType["offsite_conversion.fb_pixel_add_to_cart"] ?? 0,
          action_initiate_checkout: actionsByType["offsite_conversion.fb_pixel_initiate_checkout"] ?? 0,
          action_complete_registration: actionsByType["offsite_conversion.fb_pixel_complete_registration"] ?? 0,
          action_view_content: actionsByType["offsite_conversion.fb_pixel_view_content"] ?? 0,
          action_omni_purchase: actionsByType["omni_purchase"] ?? 0,
          action_all_leads: actionsByType["lead"] ?? 0,
          action_app_install: actionsByType["app_install"] ?? 0,
          action_flow_complete: actionsByType["onsite_conversion.flow_complete"] ?? 0,
          // Cost per action type
          cost_per_action_link_click: costByType["link_click"] ?? 0,
          cost_per_action_purchase: costByType["offsite_conversion.fb_pixel_purchase"] ?? 0,
          cost_per_action_lead: costByType["offsite_conversion.fb_pixel_lead"] ?? 0,
          // Video
          video_thruplay: videoThruplay,
          video_thruplay_rate: videoThruplayRate,
          cost_per_thruplay: costPerThruplay,
          cost_per_15_sec_video_view: costPer15sVideoView,
          cost_per_2_sec_continuous_video_view: costPer2sContinuousVideoView,
          video_p25: videoP25, video_p50: videoP50,
          video_p75: videoP75, video_p100: videoP100,
          video_2s_continuous: video2sContinuous,
          video_15s: video15s, video_30s: video30s,
          video_avg_time: videoAvgTime,
          video_plays: videoPlays,
          // Quality
          quality_ranking: qualityRanking,
          engagement_rate_ranking: engagementRateRanking,
          conversion_rate_ranking: conversionRateRanking,
          // Engagement
          inline_post_engagement: inlinePostEngagement,
          cost_per_inline_post_engagement: costPerInlinePostEngagement,
          currency,
        };

        // Story 35.5 — append 56 per-window fields (24 action counts +
        // 8 omni counts + 24 cost-per-action × window). The 32 action/omni
        // fields are catalogued in FB_ADS_METRICS; the 24 cost-per-window
        // fields are deep-dive record-only data (not in the picker palette).
        emitPerWindowRecordFields(dailyRecord, actionsByWindow, costByWindow);

        records.push(dailyRecord);
      }

      // Follow pagination
      const nextUrl = accountPageData.paging?.next;
      if (!nextUrl || rateLimitWarning) break;

      accountPageCount++;
      const nextResult = await safeFetch(nextUrl, authHeaders, 25_000);
      if (!nextResult.success) break;

      const nextAppUsage = parseAppUsage(nextResult.data);
      if (nextAppUsage > RATE_LIMIT_THRESHOLD) {
        rateLimitWarning = true;
        break;
      }

      try {
        accountPageData = (await nextResult.data.json()) as MarketingInsightsResponse;
      } catch {
        break;
      }
    }

    // --- Fetch campaign-level insights (if rate limit allows) ---
    if (!rateLimitWarning) {
      const campaignFilter = encodeURIComponent(
        JSON.stringify([{ field: "campaign.effective_status", operator: "IN", value: ["ACTIVE", "PAUSED"] }])
      );
      const campaignUrl = `${META_GRAPH_BASE}/${adAccountId}/insights?fields=${CAMPAIGN_INSIGHTS_FIELDS}&time_range=${encodeURIComponent(timeRange)}&time_increment=1&level=campaign&filtering=${campaignFilter}`;

      let url: string | null = campaignUrl;
      let pageCount = 0;

      while (url && pageCount < MAX_PAGES) {
        const result = await safeFetch(url, authHeaders, 25_000);
        if (!result.success) break;

        const response = result.data;
        const campaignAppUsage = parseAppUsage(response);
        if (campaignAppUsage > RATE_LIMIT_THRESHOLD) {
          rateLimitWarning = true;
          console.log(JSON.stringify({
            event: "meta_rate_limit_approaching",
            callCount: campaignAppUsage,
            adAccountId,
            tenantId: ctx.tenantId,
            context: "campaign_insights",
          }));
          break;
        }

        let body: MarketingInsightsResponse;
        try {
          body = (await response.json()) as MarketingInsightsResponse;
        } catch {
          break;
        }

        if (body.error) break;

        for (const row of body.data ?? []) {
          const campaignPurchaseValue = parseActionValues(row.action_values);
          const campaignSpend = parseMetricValue(row.spend);
          const campaignConversions = parseActions(row.actions).totalConversions;

          const campaignRecord: Record<string, unknown> = {
            _reportType: "campaigns",
            date: row.date_start,
            campaign_name: row.campaign_name ?? "",
            campaign_id: row.campaign_id ?? "",
            impressions: parseMetricValue(row.impressions),
            reach: parseMetricValue(row.reach),
            clicks: parseMetricValue(row.clicks),
            spend: campaignSpend,
            conversions: campaignConversions,
            purchase_value: campaignPurchaseValue,
            purchase_roas: campaignSpend > 0 ? campaignPurchaseValue / campaignSpend : 0,
            quality_ranking: row.quality_ranking ?? "",
            engagement_rate_ranking: row.engagement_rate_ranking ?? "",
            conversion_rate_ranking: row.conversion_rate_ranking ?? "",
            currency,
          };

          // Story 35.5 — ASC dynamic_attribution discriminator. When the
          // campaign is an Advantage+ Shopping Campaign, attribution is
          // dynamic and per-window splits would all be null — flag so
          // widget renderers can hide the per-window columns rather than
          // showing 4 NULL rows. Note: ASC widget rendering guidance is in
          // the adapter JSDoc; we only emit the discriminator here.
          if (isAdvantagePlusShoppingCampaign(row)) {
            campaignRecord._dynamicAttribution = true;
          }

          // Story 35.5 — feature-flagged campaign-level per-window emission.
          // Mirrors account-level per-window fields so campaign breakdowns
          // can also display attribution-window splits. Skip for ASC
          // campaigns where the per-window data is meaningless.
          if (ENABLE_PER_WINDOW_CAMPAIGN_METRICS && !campaignRecord._dynamicAttribution) {
            const campaignActionsByWindow = parseActionsByTypeWithWindows(row.actions);
            const campaignCostByWindow = parseCostByActionTypeWithWindows(row.cost_per_action_type);
            emitPerWindowRecordFields(campaignRecord, campaignActionsByWindow, campaignCostByWindow);
          }

          records.push(campaignRecord);
        }

        url = body.paging?.next ?? null;
        pageCount++;
      }
    }

    // Sort records by date
    records.sort((a, b) => String(a.date).localeCompare(String(b.date)));

    return adapterOk({
      platform: "facebook-ads",
      accountId: adAccountId,
      dateRange: { start: dateRange.start, end: dateRange.end },
      records,
      fetchedAt: new Date(),
      recordCount: records.length,
    });
  },

  // --------------------------------------------------------------------------
  // validateConnection (Subtask 1.5)
  // --------------------------------------------------------------------------
  async validateConnection(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    const adAccountId = ctx.accountId;
    if (!adAccountId) {
      return adapterErr({
        code: "account_not_found",
        message: "No Ad Account ID provided for connection validation.",
        retryable: false,
      });
    }

    // Story 35.5 — also fetch v26 attribution_setting; cached for 24h
    // so subsequent fetchMetrics() calls don't re-probe.
    const url = `${META_GRAPH_BASE}/${adAccountId}?fields=id,name,account_status,currency,attribution_setting`;
    const result = await safeFetch(
      url,
      { headers: { Authorization: `Bearer ${credentials.accessToken}` } },
      5_000
    );

    if (!result.success) {
      return handleMetaFetchError(result);
    }

    try {
      const body = (await result.data.json()) as {
        account_status?: number;
        attribution_setting?: string;
      };
      if (body.account_status !== 1) {
        return adapterErr({
          code: "account_not_found",
          message: "Ad Account is not active",
          retryable: false,
        });
      }

      // Persist attribution_setting (legacy-safe coerce on undefined).
      const setting = coerceAttributionSetting(body.attribution_setting);
      await writeAttributionSetting(ctx, setting);
    } catch {
      return adapterErr({
        code: "invalid_response",
        message: "Failed to parse Ad Account validation response",
        retryable: false,
      });
    }

    return adapterOk(true);
  },

  // --------------------------------------------------------------------------
  // discoverAccounts (Subtask 1.6)
  // --------------------------------------------------------------------------
  async discoverAccounts(
    _ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    // Story 31.20: fetch ONLY the ad-accounts section (AC #1) and require it
    // to be complete (AC #3) — a truncated catalog must never reach the
    // grant-resource upsert as success.
    const discoveryResult = await discoverMetaAccounts({
      accessToken: credentials.accessToken,
      grantedScopes: credentials.scopes,
      sections: { pages: false, adAccounts: true, instagram: false },
    });

    const adAccountsStatus = discoveryResult.sectionStatus.adAccounts;
    if (adAccountsStatus !== "complete") {
      return mapDiscoverySectionFailure(adAccountsStatus, discoveryResult.firstError);
    }

    // Defensive dedupe by id, first occurrence wins (ADR-0004 §Anti-Patterns 4).
    // id is the FROZEN act_-prefixed form (matches 31-3 backfill rows);
    // metadata.accountId carries the bare numeric account_id (AC #7).
    const accountsById = new Map<string, AccountInfo>();
    for (const account of discoveryResult.adAccounts) {
      if (accountsById.has(account.id)) continue;
      accountsById.set(account.id, {
        id: account.id,
        name: account.name,
        platform: "facebook-ads" as const,
        metadata: { currency: account.currency, accountId: account.accountId },
        // Fresh spread per row — display/consent metadata only (Story 31-4)
        scopes: [...META_INTEGRATION_SCOPES["facebook-ads"].scopes],
      });
    }

    return adapterOk([...accountsById.values()]);
  },

  // --------------------------------------------------------------------------
  // getAvailableMetrics (Subtask 1.2)
  // --------------------------------------------------------------------------
  getAvailableMetrics(): MetricDefinition[] {
    return FB_ADS_METRICS;
  },

  // --------------------------------------------------------------------------
  // getAvailableDimensions (Subtask 1.3)
  // --------------------------------------------------------------------------
  getAvailableDimensions(): DimensionDefinition[] {
    return FB_ADS_DIMENSIONS;
  },
};
